import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Droplet, Zap, Leaf, Sparkles, Cpu, Activity, MapPin, ShieldCheck, Building2, AlertTriangle, Wrench, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1716077521105-f0d699ab2047?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBnbGFzcyUyMGhvdXNlJTIwZm9yZXN0JTIwbmF0dXJlfGVufDB8fHx8MTc4MTUwMjMzOHww&ixlib=rb-4.1.0&q=85";
const NOA_AI_ICON = "https://customer-assets-jt897jd0.emergentagent.net/job_eco-motion-home/artifacts/lcr8ha4f_noa-assistant.png";
const LEAVES_IMG = "https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJpbGl0eSUyMHRleHR1cmUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MTUwMjMzOHww&ixlib=rb-4.1.0&q=85";
const SENSOR_IMG = "https://images.unsplash.com/photo-1666401565408-9b6b0741f0d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxzbWFydCUyMGhvbWUlMjBzZW5zb3IlMjBkZXZpY2V8ZW58MHx8fHwxNzgxNTAyMzM4fDA&ixlib=rb-4.1.0&q=85";
const APP_IMG = "https://images.unsplash.com/photo-1608377205619-03a0b4c4e270?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGhvbWUlMjBzZW5zb3IlMjBkZXZpY2V8ZW58MHx8fHwxNzgxNTAyMzM4fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: MapPin, eyebrow: "01 · sense", title: "Room-by-room sensing", body: "Noa devices learn your home gradually — each sensor and meter adds context to a shared floor plan you can grow at your own pace." },
  { icon: Activity, eyebrow: "02 · meter", title: "Live consumption", body: "Per-room electricity & water telemetry displayed as it arrives from your connected devices. See what's awake, what's wasting." },
  { icon: Leaf, eyebrow: "03 · save", title: "Carbon tracking", body: "Convert kWh and liters into the only number that matters: kilograms of CO₂ not emitted this month." },
  { icon: Sparkles, eyebrow: "04 · learn", title: "AI sustainability coach", body: "NOA AI — an upcoming intelligent assistant that will offer sustainability tips tailored to your home patterns. Coming soon." },
];

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", home_size: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Waitlist count intentionally not displayed publicly — no unsupported traction numbers.
  }, []);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/waitlist", form);
      toast.success("You're on the list. A team member will reach out soon.");
      setForm({ name: "", email: "", home_size: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-bone min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-bone/30 via-bone/60 to-bone" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32 md:pt-32 md:pb-48 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 animate-fade-up">
            <p className="label-eyebrow mb-6" data-testid="hero-eyebrow">Smart home, lighter footprint</p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-forest">
              Your home,<br />
              <em className="not-italic text-terracotta">tuned</em> to the planet.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ink/70 max-w-2xl leading-relaxed">
              Noa connects compatible smart sensors, meters, and devices to detect energy and water waste, automate everyday usage, and show measurable savings through one intelligent platform.
            </p>
            <p className="mt-4 text-base md:text-lg text-ink/60 max-w-2xl leading-relaxed">
              Start with a small smart-device kit and expand room by room, without expensive renovations.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button className="bg-forest text-bone hover:bg-forest-hover rounded-full px-8 py-6 text-base font-medium">
                  Start saving <ArrowUpRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a href="#waitlist" data-testid="hero-cta-secondary">
                <Button variant="outline" className="rounded-full border-forest text-forest hover:bg-forest hover:text-bone px-8 py-6 text-base">
                  Join the waitlist
                </Button>
              </a>
            </div>
          </div>

          <div className="md:col-span-4 animate-fade-up noa-hero-stage" style={{ animationDelay: "120ms" }}>
            <NoaAiHeroVisual />
            <div className="surface-card p-8 flex flex-col items-start justify-center min-h-[220px] mt-6" data-testid="coming-soon-card">
              <p className="label-eyebrow">Live impact tracker</p>
              <p className="font-serif text-4xl md:text-5xl text-forest font-light mt-4 leading-none">Coming soon.</p>
              <p className="mt-4 text-sm text-ink/60 leading-relaxed">
                Real-time savings across the Noa network — kilowatts, liters and kilograms of CO₂ avoided — will surface here once we power on the first wave of homes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5 md:row-span-2 bg-bonewarm rounded-3xl border border-[#E2DFD8] p-10 flex flex-col justify-between min-h-[420px]">
            <div>
              <p className="label-eyebrow mb-4">Why Noa</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-forest tracking-tight leading-[1.05]">
                Sustainability that doesn't ask you to try harder.
              </h2>
            </div>
            <p className="text-ink/70 leading-relaxed text-base mt-8">
              Most eco-apps ask you to log, track, weigh, remember. We just listen — to motion, to flow, to draw — and quietly nudge the home back into balance while you live in it.
            </p>
          </div>

          {features.map((f, i) => (
            <div key={f.eyebrow} className={`surface-card p-8 md:col-span-${i < 2 ? 7 : 7}`}>
              <div className="flex items-start justify-between gap-6">
                <div>
                  <p className="label-eyebrow mb-3">{f.eyebrow}</p>
                  <h3 className="font-serif text-3xl text-ink font-medium tracking-tight">{f.title}</h3>
                  <p className="mt-3 text-ink/65 max-w-md leading-relaxed">{f.body}</p>
                </div>
                <div className="shrink-0 w-12 h-12 rounded-full bg-forest/5 text-forest grid place-items-center">
                  <f.icon size={20} strokeWidth={1.5} />
                </div>
              </div>
            </div>
          )).slice(0, 2)}

          <div className="md:col-span-7 relative rounded-3xl overflow-hidden min-h-[280px] bg-forest">
            <img src={LEAVES_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
            <div className="relative p-10 h-full flex flex-col justify-end">
              <p className="label-eyebrow text-bone/70 mb-3">04 · learn</p>
              <h3 className="font-serif text-4xl text-bone font-light">Coach in your pocket.</h3>
              <p className="text-bone/70 mt-3 max-w-md leading-relaxed">NOA AI — an upcoming intelligent assistant for sustainable living. Ask things like "how do I cut my shower bill?" and get a personalised plan. Coming soon.</p>
            </div>
          </div>

          {features.slice(2).map((f) => (
            <div key={f.eyebrow} className="surface-card p-8 md:col-span-6">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <p className="label-eyebrow mb-3">{f.eyebrow}</p>
                  <h3 className="font-serif text-3xl text-ink font-medium tracking-tight">{f.title}</h3>
                  <p className="mt-3 text-ink/65 max-w-md leading-relaxed">{f.body}</p>
                </div>
                <div className="shrink-0 w-12 h-12 rounded-full bg-forest/5 text-forest grid place-items-center">
                  <f.icon size={20} strokeWidth={1.5} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-bonewarm py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="label-eyebrow mb-6">How it works</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-forest tracking-tight leading-[1.05]">
              A small kit.<br />Minutes to set up.<br />A quieter home.
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 gap-4">
            {[
              { n: "01", icon: Cpu, title: "Start with a kit", body: "Choose a small starter set of Noa-compatible devices — plug meters, motion sensors, a leak sensor — and pair them in the app in minutes." },
              { n: "02", icon: MapPin, title: "Let Noa map your home", body: "As you add devices, Noa builds a live picture of your home — room by room — using motion, water flow and energy signals." },
              { n: "03", icon: ShieldCheck, title: "Live in the saving", body: "The app proposes gentle adjustments. You approve once, savings compound forever." },
            ].map((s) => (
              <div key={s.n} className="surface-card p-6 flex items-start gap-6">
                <div className="font-mono text-xs text-terracotta font-semibold pt-1">{s.n}</div>
                <div className="w-10 h-10 rounded-full bg-forest text-bone grid place-items-center shrink-0">
                  <s.icon size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-serif text-2xl font-medium text-ink">{s.title}</h4>
                  <p className="text-ink/65 text-sm mt-1 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL IMAGE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-3xl overflow-hidden aspect-[4/5] relative">
          <img src={SENSOR_IMG} alt="Sensor hardware" className="w-full h-full object-cover" />
          <div className="absolute bottom-6 left-6 right-6 text-bone">
            <p className="label-eyebrow text-bone/80">The hardware</p>
            <p className="font-serif text-3xl mt-2">Smaller than your coffee cup.</p>
          </div>
        </div>
        <div className="rounded-3xl overflow-hidden aspect-[4/5] relative">
          <img src={APP_IMG} alt="App in hand" className="w-full h-full object-cover" />
          <div className="absolute bottom-6 left-6 right-6 text-bone">
            <p className="label-eyebrow text-bone/80">The app</p>
            <p className="font-serif text-3xl mt-2">Built for glance, not grind.</p>
          </div>
        </div>
      </section>

      {/* PROPERTY MANAGERS */}
      <section id="property-managers" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        <div className="rounded-3xl bg-bonewarm border border-[#E2DFD8] p-10 md:p-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-6">
              <div className="flex items-center gap-2 mb-4">
                <p className="label-eyebrow">For Property Managers</p>
                <span className="text-[10px] font-mono uppercase tracking-widest bg-white border border-[#E2DFD8] text-forest px-2 py-0.5 rounded-full">
                  in development
                </span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-forest tracking-tight leading-[1.05]">
                Reduce waste across every building and unit from one dashboard.
              </h2>
              <p className="mt-6 text-lg text-ink/70 max-w-xl leading-relaxed">
                Noa helps property managers monitor energy and water usage, detect leaks and unusual consumption, automate alerts, and coordinate maintenance across multiple buildings and units.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#waitlist" data-testid="pm-cta-pilot">
                  <Button className="bg-forest text-bone hover:bg-forest-hover rounded-full px-8 py-6 text-base font-medium">
                    <Building2 size={18} className="mr-2" /> Request a Property Pilot
                  </Button>
                </a>
                <Link to="/property-billing" data-testid="pm-cta-billing" className="self-center text-sm text-forest hover:text-forest-hover underline underline-offset-4 decoration-forest/30">
                  See sample dashboard billing →
                </Link>
              </div>
            </div>

            <div className="md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="pm-benefits">
              {[
                { icon: Building2, title: "Building and unit-level monitoring", body: "Track energy and water usage per building, per unit — roll up or drill down as needed." },
                { icon: AlertTriangle, title: "Leak and abnormal-usage alerts", body: "Get notified the moment consumption patterns break the norm." },
                { icon: Wrench, title: "Maintenance ticket assignment", body: "Turn alerts into work orders and route them to the right technician." },
                { icon: BarChart3, title: "Sustainability and savings reports", body: "Monthly ESG-ready reports summarising savings, carbon and interventions." },
              ].map((b) => (
                <div key={b.title} className="bg-white rounded-2xl border border-[#E2DFD8] p-5 flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-md">
                  <div className="w-10 h-10 rounded-full bg-forest/5 text-forest grid place-items-center mb-3">
                    <b.icon size={18} strokeWidth={1.5} />
                  </div>
                  <p className="font-serif text-lg text-ink font-medium leading-tight">{b.title}</p>
                  <p className="mt-2 text-sm text-ink/60 leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section id="waitlist" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="surface-card p-10 md:p-14">
          <p className="label-eyebrow mb-4">Be among the first to experience NOA</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-forest tracking-tight">Join the waitlist.</h2>
          <p className="mt-3 text-ink/65 max-w-xl">Join the waitlist for upcoming NOA pilot opportunities.</p>

          <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="waitlist-form">
            <Input
              placeholder="Your name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white"
              data-testid="waitlist-name"
            />
            <Input
              type="email"
              placeholder="Email address"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white"
              data-testid="waitlist-email"
            />
            <Select value={form.home_size} onValueChange={(v) => setForm({ ...form, home_size: v })}>
              <SelectTrigger className="rounded-xl border-[#E2DFD8] bg-white md:col-span-2" data-testid="waitlist-home-size">
                <SelectValue placeholder="Home size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="studio">Studio / 1BR</SelectItem>
                <SelectItem value="small">2-3 bedrooms</SelectItem>
                <SelectItem value="medium">4-5 bedrooms</SelectItem>
                <SelectItem value="large">6+ bedrooms</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Anything we should know? (optional)"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white md:col-span-2 min-h-24"
              data-testid="waitlist-message"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="bg-forest hover:bg-forest-hover text-bone rounded-full px-8 py-6 md:col-span-2 mt-2 disabled:opacity-60"
              data-testid="waitlist-submit"
            >
              {submitting ? "Sending…" : "Request invite"}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-serif text-2xl text-forest leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-ink/50 mt-1">{label}</p>
    </div>
  );
}

/**
 * NoaAiHeroVisual — premium presentation of the canonical NOA AI icon.
 * Icon asset is used verbatim. Animations are pure CSS + a tiny desktop-only
 * pointer parallax. prefers-reduced-motion disables all motion via index.css.
 */
function NoaAiHeroVisual() {
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    if (media.matches || isCoarse) return; // no parallax on touch or reduced motion

    const el = wrapRef.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTilt({ x: Math.max(-1, Math.min(1, dx)), y: Math.max(-1, Math.min(1, dy)) }));
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const parallaxStyle = {
    transform: `translate3d(${tilt.x * 6}px, ${tilt.y * 6}px, 0)`,
    transition: "transform 200ms ease-out",
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full flex flex-col items-center justify-center py-6"
      data-testid="noa-ai-hero-visual"
      aria-label="NOA AI assistant — interface preview"
    >
      <div className="relative w-48 h-48 md:w-52 md:h-52" style={parallaxStyle}>
        {/* Soft breathing glow behind the icon */}
        <div
          className="absolute inset-0 rounded-full noa-icon-glow pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(109,135,100,0.55) 0%, rgba(27,58,44,0.25) 45%, rgba(27,58,44,0) 70%)",
            filter: "blur(18px)",
          }}
        />
        {/* Subtle outer ring */}
        <div className="absolute inset-2 rounded-full border border-forest/15 pointer-events-none" />
        {/* The floating + breathing icon (asset unchanged) */}
        <div className="absolute inset-0 flex items-center justify-center noa-icon-float">
          <div className="noa-icon-breathe">
            <img
              src={NOA_AI_ICON}
              alt="NOA AI assistant"
              className="w-40 h-40 md:w-44 md:h-44 object-contain drop-shadow-[0_10px_30px_rgba(27,58,44,0.25)] select-none"
              draggable={false}
            />
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-moss animate-pulse" />
        <p className="label-eyebrow">NOA AI · interface preview</p>
      </div>
      <p className="mt-2 text-xs text-ink/50 max-w-[260px] text-center leading-relaxed">
        Visual presentation of the upcoming NOA assistant. Live AI backend not yet enabled.
      </p>
    </div>
  );
}
